import { TextareaHTMLAttributes, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { cva } from 'class-variance-authority';
import { cn } from '../../libs';
import { ControlHookFormError } from '../../types';
import { FormErrorMessage } from '../FormErrorMessage';

export interface TextareaProps extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'>, ControlHookFormError {
  /**
   * Placeholder of the textarea.
   */
  placeholder?: string;
  /**
   * Color of the textarea.
   */
  color?: 'primary' | 'danger';
  /**
   * Size of the textarea.
   */
  size?: 'md';
  /**
   * Value of the textarea.
   */
  value?: string;
  /**
   * Default value of the textarea.
   */
  defaultValue?: string;
}

export const TEXT_AREA_VARIANTS = cva(
  [
    'flex w-full items-center rounded-md border bg-fg-primary text-text-secondary shadow-xs transition-colors placeholder:transition-colors',
    'placeholder:text-text-quarterary',
    'hover:placeholder:text-text-quarterary-hover',
    'focus:border-border-brand-filled',
    'focus:placeholder:text-text-quarterary-hover',
    'focus:text-text-primary',
    'focus:ring-2',
    'focus:ring-brand',
    'disabled:bg-fg-disabled',
    'disabled:border-border-disabled',
    'disabled:text-text-disabled',
    'disabled:placeholder:text-text-disabled',
    'disabled:ring-0',
    'disabled:pointer-events-none',
    'disabled:shadow-none',
  ],
  {
    variants: {
      color: {
        primary: ['border-border-primary', 'hover:border-border-primary-hover'],
        danger: ['border-border-error', 'ring-2 ring-error', 'hover:border-border-error-solid'],
      },
      size: {
        md: 'min-h-16 p-3 text-paragraph-sm leading-4',
      },
      defaultVariants: {
        color: 'primary',
        size: 'md',
      },
    },
  },
);

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ color = 'primary', size = 'md', error, value, defaultValue, ...rest }, ref) => {
    const innerRef = useRef<HTMLTextAreaElement>(null);
    const [length, setLength] = useState<number>(0);

    useEffect(() => {
      if (rest.maxLength) {
        const element = innerRef.current;

        if (!element) {
          return;
        }

        const updateLength = () => {
          setLength(element.value.length);
        };

        updateLength();
        element.addEventListener('input', updateLength);

        return () => {
          element.removeEventListener('input', updateLength);
        };
      }
    }, [rest.maxLength]);

    /**
     * Update length when value changes externally (e.g., form reset)
     */
    useEffect(() => {
      const element = innerRef.current;
      if (element && (value !== undefined || defaultValue !== undefined)) {
        setLength(element.value.length);
      }
    }, [value, defaultValue]);

    useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(ref, () => innerRef.current, []);

    return (
      <div className="relative">
        <textarea
          {...rest}
          value={value}
          defaultValue={defaultValue}
          ref={innerRef}
          className={cn(TEXT_AREA_VARIANTS({ color, size }), rest.className)}
        />
        {rest.maxLength && (
          <span
            className={cn(
              'pointer-events-none absolute bottom-1 right-2.5 rounded-md bg-fg-primary px-1 text-text-quarterary transition-colors',
              length >= rest.maxLength && 'text-text-error-primary',
            )}
          >
            {length}/{rest.maxLength}
          </span>
        )}
        <AnimatePresence>{error && <FormErrorMessage>{error}</FormErrorMessage>}</AnimatePresence>
      </div>
    );
  },
);
